import { AppSidebar } from "@/components/AppSidebar";
import { SiteHeader } from "@/components/SiteHeder";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { DashboardStatCard } from "@/components/stats/DashboardStatCard";
import { UserGrowthChart } from "@/components/stats/UserGrowthChart";
import { VIPDistributionChart } from "@/components/stats/VIPDistributionChart";
import { LoginHoursChart } from "@/components/stats/LoginHoursChart";
import { useAdminStatsStore } from "@/stores/adminStats";
import { useEffect } from "react";
import { Users, Crown, UserPlus, LogIn, Activity, RefreshCw } from "lucide-react";

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2].map((i) => (
          <Card key={i} className="h-80">
            <CardHeader>
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-full w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { loading, dashboard, userGrowth, loginDistribution, fetchAll } =
    useAdminStatsStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <SidebarProvider>
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title="仪表盘" />
        <div className="flex-1 p-6 space-y-6">
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              刷新
            </Button>
          </div>

          {loading && !dashboard ? (
            <LoadingSkeleton />
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <DashboardStatCard
                  title="总用户数"
                  value={dashboard?.totalUsers?.toLocaleString() ?? "-"}
                  icon={Users}
                  description={`今日新增 ${dashboard?.todayNewUsers ?? 0} 人`}
                />
                <DashboardStatCard
                  title="VIP 用户"
                  value={(dashboard?.totalVips ?? 0).toLocaleString()}
                  icon={Crown}
                  description={`VVIP ${dashboard?.totalVvips ?? 0} 人`}
                />
                <DashboardStatCard
                  title="今日新增"
                  value={dashboard?.todayNewUsers ?? 0}
                  icon={UserPlus}
                  description={`邀请注册 ${dashboard?.todayInvited ?? 0} 人`}
                />
                <DashboardStatCard
                  title="今日登录"
                  value={dashboard?.todayLogins ?? 0}
                  icon={LogIn}
                  description="登录次数"
                />
                <DashboardStatCard
                  title="今日活跃"
                  value={dashboard?.todayActiveUsers ?? 0}
                  icon={Activity}
                  description="活跃用户数"
                />
              </div>

              <UserGrowthChart data={userGrowth} />

              <div className="grid gap-4 md:grid-cols-2">
                <VIPDistributionChart data={dashboard?.vipDistribution} />
                <LoginHoursChart data={loginDistribution} />
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
